import { createApi } from '@reduxjs/toolkit/query/react';
import { CHATTER_PEER_LIST, CHATTER_PEER_CREATE, CHATTER_PEER_DELETE, CHATTER_PEER_DETAILS, CHATTER_PEER_EDIT, CHATTER_PEER_REFERENCE_LETTER, CHATTER_PEER_REFERENCE_LETTER_CREATE, CHATTER_PEER_REFERENCE_LETTER_SEND, CHATTER_PEER_REFERENCE_LETTER_REMOVE, CHATTER_PEER_GROUPS_ASSIGNED, CHATTER_PEER_PROGRAMS_LIST, CHATTER_PEER_ATTENDANCE_LIST, CHATTER_PEER_REFERENCE_LETTER_CREATE_V2 } from "config/apiPaths";
import axiosBaseQuery from "utils/axios/axiosBaseQuery";

export const chatterPeerApi = createApi({
    reducerPath: 'chatterPeerApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/client/peer",
    }),
    tagTypes: ["ChatterPeer", "ChatterPeerDetails"],
    endpoints: (builder) => ({

        // get peer list
        getChatterPeerList: builder.query({
            query: ({ search = "", status = "", program_id = "", page = 1, limit = 10, sort = { column: "id", direction: "ASC" } }) => ({
                method: "PATCH",
                url: CHATTER_PEER_LIST,
                data: { search, status, page, limit, program_id, sort },
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["ChatterPeer"]
        }),

        // create chatter peer
        createChatterPeer: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: CHATTER_PEER_CREATE,
                data
            }),
            invalidatesTags: ["ChatterPeer"]
        }),

        // delete chatter program
        deleteChatterPeer: builder.mutation({
            query: (id) => ({
                method: "DELETE",
                url: `${CHATTER_PEER_DELETE}/${id}`
            }),
            invalidatesTags: ["ChatterPeer", "ChatterPeerDetails", "ChatterPeerGroups"]
        }),

        // chatter program details
        chatterPeerDetails: builder.query({
            query: (id) => ({
                method: "GET",
                url: `${CHATTER_PEER_DETAILS}/${id}`
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["ChatterPeerDetails", "ChatterPeerGroups"]
        }),

        // update chatter peer
        updateChatterPeer: builder.mutation({
            query: ({ data, peerId }) => ({
                method: "PUT",
                url: `${CHATTER_PEER_EDIT}/${peerId}`,
                data
            }),
            invalidatesTags: ["ChatterPeer", "ChatterPeerDetails", "ChatterPeerGroups"]
        }),

        // get reference letter
        getPeerReferenceLetter: builder.query({
            query: () => ({
                method: "GET",
                url: CHATTER_PEER_REFERENCE_LETTER
            }),
            transformResponse: (response) => {
                return response.data;
            }
        }),

        // create peer reference letter
        createReferenceLetter: builder.mutation({
            query: ({ peerId, data }) => ({
                method: "PUT",
                url: `${CHATTER_PEER_REFERENCE_LETTER_CREATE}/${peerId}`,
                data
            }),
            invalidatesTags: ["ChatterPeerDetails", "ChatterPeerGroups"]
        }),

        createReferenceLetterV2: builder.mutation({
            query: ({ peerId, data }) => ({
                method: "PUT",
                url: `${CHATTER_PEER_REFERENCE_LETTER_CREATE_V2}/${peerId}`,
                data
            }),
            invalidatesTags: ["ChatterPeerDetails", "ChatterPeerGroups"]
        }),

        // send reference letter
        sendReferenceLetter: builder.mutation({
            query: ({ peerId,refId, email }) => ({
                method: "POST",
                url: `${CHATTER_PEER_REFERENCE_LETTER_SEND}/${peerId}/${refId}`,
                data: { email }
            })
        }),
        // delete reference letter
        deleteReferenceLetter: builder.mutation({
            query: ({ referenceId, peerId }) => ({
                method: "DELETE",
                url: `${CHATTER_PEER_REFERENCE_LETTER_REMOVE}/${referenceId}/${peerId}`,
            })
        }),

        getChatterPeerGroupsList: builder.query({
            query: ({ search = "", status = "", id = "", page = 1, limit = 10, sort = { column: "id", direction: "DESC" } }) => ({
                method: "PATCH",
                url: `${CHATTER_PEER_GROUPS_ASSIGNED}/${id}`,
                data: { search, status, page, limit, sort },
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["ChatterPeerGroups"]
        }),

        getChatterPeerProgramsList: builder.query({
            query: ({ peerId }) => ({
                method: "GET",
                url: `${CHATTER_PEER_PROGRAMS_LIST}/${peerId}`,
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["peerChatterPrograms"]
        }),

        getChatterPeerAttendanceList: builder.query({
            query: ({ programId, peerId }) => ({
                method: "GET",
                url: `${CHATTER_PEER_ATTENDANCE_LIST}/${programId}/${peerId}`,
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["peerAttendanceList"]
        }),
    })
})

export const {
    useGetChatterPeerListQuery,
    useCreateChatterPeerMutation,
    useDeleteChatterPeerMutation,
    useChatterPeerDetailsQuery,
    useUpdateChatterPeerMutation,
    useGetPeerReferenceLetterQuery,
    useCreateReferenceLetterMutation,
    useSendReferenceLetterMutation,
    useDeleteReferenceLetterMutation,
    useGetChatterPeerGroupsListQuery,
    useGetChatterPeerProgramsListQuery,
    useLazyGetChatterPeerAttendanceListQuery,
    useCreateReferenceLetterV2Mutation
} = chatterPeerApi;